import * as React from 'react';
import { T, Var } from 'gt-react';
import { CheckIcon, CaretUpDownIcon, XIcon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useIsInsideDialog } from '@/components/ui/dialog';

const DEFAULT_MAX_DISPLAY_ITEMS = 2;

export type AutocompletePropsBase<
  T,
  TMultiSelect extends boolean | undefined,
> = {
  options: T[];
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  getValue: (option: T) => string;
  getLabel: (option: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  maxDisplayItems?: number;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  filterFunction?: (option: T, search: string) => boolean;
  allowClear?: boolean;
  /**
   * Opt-in incremental loading for server-paged option lists: the dropdown
   * asks for the next batch when scrolled near the bottom, and offers a
   * button as the explicit affordance. Omit for fully-loaded lists.
   */
  loadMore?: {
    hasMore: boolean;
    isLoading?: boolean;
    onLoadMore: () => void;
    /** Shown instead of the button when more exist but can't be loaded. */
    exhaustedMessage?: React.ReactNode;
  };
  /**
   * Multi-select only: wrap ALL selected pills across lines and let the
   * trigger grow vertically, instead of one truncating row with a "+N more"
   * tail. Use where the selection is the point (forms), not for compact
   * filter bars.
   */
  wrapSelected?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Radix `modal` for the dropdown's Popover. Defaults to true inside a Dialog,
   * where a non-modal popover is portaled outside the dialog's scroll lock and
   * so can't be scrolled with the wheel; false elsewhere, so a plain page
   * doesn't get its scrollbar removed every time a dropdown opens.
   */
  popoverModal?: boolean;
  multiSelect: TMultiSelect;
  onValueChange: TMultiSelect extends true
    ? (values: string[]) => void
    : (value: string) => void;
  value: (TMultiSelect extends true ? string[] : string) | undefined;
  renderTrigger?: TMultiSelect extends true
    ? (values: T[]) => React.ReactNode
    : (value: T) => React.ReactNode;
};

type AutocompleteProps<
  T,
  TProps extends AutocompletePropsBase<T, boolean | undefined> =
    AutocompletePropsBase<T, boolean | undefined>,
> =
  TProps extends AutocompletePropsBase<T, infer U>
    ? U extends boolean | undefined
      ? AutocompletePropsBase<T, U>
      : never
    : never;

type AutocompleteItemProps<T, TMultiSelect extends boolean> = Pick<
  AutocompletePropsBase<T, TMultiSelect>,
  'getValue' | 'getLabel' | 'renderOption'
> & {
  option: T;
  isSelected: boolean;
  onSelect: () => void;
};

// Memoized item component for performance
function AutocompleteItemBase<T, TMultiSelect extends boolean>({
  getValue,
  getLabel,
  option,
  isSelected,
  onSelect,
  renderOption,
}: AutocompleteItemProps<T, TMultiSelect>) {
  const handleSelect = React.useCallback(() => {
    onSelect();
  }, [onSelect]);

  // Fallback to renderOption for backward compatibility
  const content = renderOption ? (
    renderOption(option, isSelected)
  ) : (
    <>
      <CheckIcon
        className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
      />
      <span>{getLabel(option)}</span>
    </>
  );

  return (
    <CommandItem
      key={getValue(option)}
      value={getValue(option)}
      onSelect={handleSelect}
    >
      {content}
    </CommandItem>
  );
}

const AutocompleteItem = React.memo(
  AutocompleteItemBase,
) as typeof AutocompleteItemBase;

function isValueArray(value: string | string[] | undefined): value is string[] {
  return value !== undefined && typeof value !== 'string';
}

function Trigger<T>({
  selectedOptions,
  placeholder,
  multiSelect,
  getLabel,
  getValue,
  renderTrigger,
  wrapSelected,
  onRemove,
}: Pick<
  AutocompleteProps<T>,
  | 'placeholder'
  | 'multiSelect'
  | 'maxDisplayItems'
  | 'getLabel'
  | 'getValue'
  | 'renderTrigger'
  | 'wrapSelected'
> & {
  selectedOptions: T[];
  /** Wrapped-pill mode: remove one selected value via its pill's ✕. */
  onRemove: (value: string) => void;
}) {
  if (selectedOptions.length === 0) {
    return (
      <span className="text-muted-foreground truncate">{placeholder}</span>
    );
  }

  // If renderTrigger is provided, use it (for both single and multi select)
  if (renderTrigger) {
    const customContent = multiSelect
      ? (renderTrigger as (values: T[]) => React.ReactNode)(selectedOptions)
      : (renderTrigger as (value: T) => React.ReactNode)(selectedOptions[0]);

    return <span className="truncate">{customContent}</span>;
  }

  // Default rendering for multiSelect
  if (multiSelect) {
    if (wrapSelected) {
      // Every pill visible; the trigger grows vertically as pills wrap. Each
      // pill carries a ✕ to remove just that value. The ✕ is a span (the
      // whole trigger is already a <button>, so no nesting) and stops the
      // pointer events so removing doesn't also toggle the dropdown.
      // Pills render alphabetically regardless of click order, so a value
      // picked later still lands in its sorted place.
      const sortedOptions = [...selectedOptions].sort((a, b) =>
        getLabel(a).localeCompare(getLabel(b)),
      );
      return (
        <div className="flex min-w-0 flex-1 flex-wrap gap-1 py-0.5">
          {sortedOptions.map((option) => (
            <span
              key={getValue(option)}
              className="bg-secondary text-secondary-foreground inline-flex max-w-full items-center gap-1 rounded px-2 py-0.5 text-xs"
            >
              <span className="min-w-0 truncate">{getLabel(option)}</span>
              <span
                role="button"
                tabIndex={0}
                aria-label={`Remove ${getLabel(option)}`}
                className="shrink-0 cursor-pointer rounded-sm opacity-60 hover:opacity-100"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove(getValue(option));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(getValue(option));
                  }
                }}
              >
                <XIcon className="h-3 w-3" />
              </span>
            </span>
          ))}
        </div>
      );
    }
    return (
      <div className="flex gap-1 overflow-hidden">
        {selectedOptions
          .slice(0, Math.min(selectedOptions.length, 3))
          .map((option) => (
            <span
              key={getValue(option)}
              className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-xs whitespace-nowrap flex-shrink-0"
            >
              {getLabel(option)}
            </span>
          ))}
        {selectedOptions.length > 3 && (
          <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-xs whitespace-nowrap flex-shrink-0">
            <T context="count of additional selected items not shown">
              +<Var>{selectedOptions.length - 3}</Var> more
            </T>
          </span>
        )}
      </div>
    );
  }

  // Default rendering for single select
  return <span className="truncate">{getLabel(selectedOptions[0])}</span>;
}

export function Autocomplete<T>({
  options,
  value,
  searchValue,
  onSearchValueChange,
  getValue,
  getLabel,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search options...',
  emptyMessage = 'No results.',
  className,
  disabled = false,
  multiSelect,
  maxDisplayItems = DEFAULT_MAX_DISPLAY_ITEMS,
  renderOption,
  renderTrigger,
  filterFunction,
  allowClear = false,
  loadMore,
  wrapSelected = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  popoverModal,
}: AutocompleteProps<T>) {
  const isInsideDialog = useIsInsideDialog();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [_highlightedValue, setHighlightedValue] = React.useState('');

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  // When the search text changes, drop any stale manual highlight so the
  // highlight falls back to the first filtered option (see `highlightedValue`
  // below). Without this, a highlight value from a previous query no longer
  // matches any visible item and cmdk shows nothing selected until the user
  // arrows down — so Enter/Down on a fresh search wouldn't pick the top hit.
  const [prevSearchValue, setPrevSearchValue] = React.useState(searchValue);
  if (searchValue !== prevSearchValue) {
    setPrevSearchValue(searchValue);
    setHighlightedValue('');
  }

  // Normalize value to always be an array for easier handling
  const selectedValues: string[] = React.useMemo(() => {
    if (isValueArray(value)) {
      return value;
    }
    return value ? [value] : [];
  }, [value]);

  const selectedOptions = React.useMemo(() => {
    return options.filter((option) =>
      selectedValues.includes(getValue(option)),
    );
  }, [options, selectedValues, getValue]);

  // Default filter function
  const defaultFilterFunction = React.useCallback(
    (option: T, search: string) => {
      return (
        getLabel(option).toLowerCase().includes(search.toLowerCase()) ||
        getValue(option).toLowerCase().includes(search.toLowerCase())
      );
    },
    [getLabel, getValue],
  );

  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return options;
    const filterFn = filterFunction || defaultFilterFunction;
    return options.filter((option) => filterFn(option, searchValue));
  }, [options, searchValue, filterFunction, defaultFilterFunction]);

  // Keep the first filtered option highlighted so Enter selects it
  const highlightedValue: string = React.useMemo(() => {
    if (_highlightedValue) return _highlightedValue;
    return filteredOptions.length ? getValue(filteredOptions[0]) : '';
  }, [filteredOptions, getValue, _highlightedValue]);

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        const newValues: string[] = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onValueChange(newValues);
      } else {
        const newValue = selectedValues.includes(optionValue)
          ? ''
          : optionValue;
        (onValueChange as (value: string) => void)(newValue);
        setOpen(false);
      }
    },
    [selectedValues, multiSelect, onValueChange, setOpen],
  );

  // Pull the next batch in as the list nears its end, so scrolling is enough
  // and the button below is only a fallback affordance.
  const handleListScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!loadMore?.hasMore || loadMore.isLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 48) {
        loadMore.onLoadMore();
      }
    },
    [loadMore],
  );

  const handleClear = React.useCallback(() => {
    if (multiSelect) {
      onValueChange([]);
    } else {
      (onValueChange as (value: string) => void)('');
    }
  }, [onValueChange, multiSelect]);

  // Wrapped-pill ✕: drop one value without opening the dropdown.
  const handleRemove = React.useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        onValueChange(selectedValues.filter((v) => v !== optionValue));
      }
    },
    [multiSelect, onValueChange, selectedValues],
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      modal={popoverModal ?? isInsideDialog}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between bg-transparent min-w-0',
            wrapSelected ? 'h-auto min-h-8 whitespace-normal' : 'h-8',
            className,
          )}
        >
          <Trigger
            selectedOptions={selectedOptions}
            placeholder={placeholder}
            multiSelect={multiSelect}
            maxDisplayItems={maxDisplayItems}
            getLabel={getLabel}
            getValue={getValue}
            renderTrigger={renderTrigger}
            wrapSelected={wrapSelected}
            onRemove={handleRemove}
          />
          <CaretUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          shouldFilter={false}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={onSearchValueChange}
          />
          <CommandList onScroll={handleListScroll}>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(getValue(option));
                return (
                  <AutocompleteItem
                    key={`${getLabel(option)}::${getValue(option)}`}
                    option={option}
                    getLabel={getLabel}
                    getValue={getValue}
                    isSelected={isSelected}
                    onSelect={() => handleSelect(getValue(option))}
                    renderOption={renderOption}
                  />
                );
              })}
            </CommandGroup>
            {loadMore?.hasMore ? (
              <CommandGroup>
                <CommandItem
                  disabled={loadMore.isLoading}
                  // Keep the dropdown open: this extends the list rather than
                  // picking from it.
                  onSelect={loadMore.onLoadMore}
                  className="justify-center text-sm text-muted-foreground"
                >
                  {loadMore.isLoading ? (
                    <T context="autocomplete loading the next batch of options">
                      Loading…
                    </T>
                  ) : (
                    <T context="button label to load more autocomplete options">
                      Load more
                    </T>
                  )}
                </CommandItem>
              </CommandGroup>
            ) : loadMore?.exhaustedMessage ? (
              <div className="px-2 py-1.5 text-center text-xs text-muted-foreground">
                {loadMore.exhaustedMessage}
              </div>
            ) : null}
            {allowClear && (
              <CommandGroup className="sticky bottom-0 bg-background">
                <CommandSeparator />
                <CommandItem
                  onSelect={() => {
                    handleClear();
                    setOpen(false);
                  }}
                  className="text-sm justify-center"
                >
                  <span>
                    <T context="button label to clear autocomplete selection">
                      Clear
                    </T>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
